import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  Paper,
  alpha,
  Divider,
  Button,
  Stack,
  Snackbar,
  Alert,
  Avatar,
  Checkbox,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Close,
  ZoomIn,
  LocationOn,
  Send,
  CheckCircle,
  Image as ImageIcon,
  Map as MapIcon,
  AccessTime,
  ThumbUp,
  ThumbDown,
  PersonAdd,
  Comment,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  ChevronLeft,
  ChevronRight,
  SmartToy,
  PictureAsPdf,
} from '@mui/icons-material';
import { caseService } from '../services/api';
import ImageLocationMap from './maps/ImageLocationMap';
import LocationListSidebar from './maps/LocationListSidebar';
import { useTranslation } from 'react-i18next';

// Get API URL from the same place the API service uses it
const host = window.location.hostname;
const API_URL = `https://${host}:5000`;
const hasAiAssessment = image => Boolean([
  image.aiQualityCheck,
  image.aiDamageAssessment,
  image.aiObservation,
  image.additionalRemarks,
].some(value => value !== null && value !== undefined && String(value).trim()));

// Component to display a modern gallery of captured images with map integration
const ProcessedCaseView = ({ open, onClose, onStatusChanged, caseData, readOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [magnifier, setMagnifier] = useState({ visible: false, x: 50, y: 50, left: 0, top: 0 });
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Action dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [remarks, setRemarks] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // States for image reassignment
  const [selectAllImages, setSelectAllImages] = useState(false);
  const [selectedImagesToRecapture, setSelectedImagesToRecapture] = useState([]);
  const [individualRemarks, setIndividualRemarks] = useState({});
  const [isReassignMode, setIsReassignMode] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignmentDialogOpen, setReassignmentDialogOpen] = useState(false);

  // States for individual image remarks
  const [imageRemarks, setImageRemarks] = useState({});
  // States for version history
  const [versionHistoryDialog, setVersionHistoryDialog] = useState({
    open: false,
    photoType: null,
    currentVersion: null,
    versions: []
  });
  // Google Maps states
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Case locking states
  const [isLocked, setIsLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState('');
  const [lockedTime, setLockedTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(15 * 60); // 15 minutes in seconds
  const lockTimerRef = useRef(null);
  const [resumeLockDialogOpen, setResumeLockDialogOpen] = useState(false);
  const [lockConflict, setLockConflict] = useState(null);
  const [lockBlocked, setLockBlocked] = useState(true);
  const lockPromptShownRef = useRef(false);

  // Get marker color based on image type
  const getMarkerColor = useCallback((imageType) => {
    const type = imageType?.toLowerCase() || '';
    if (type.includes('front')) return 'blue';
    if (type.includes('rear')) return 'green';
    if (type.includes('left')) return 'yellow';
    if (type.includes('right')) return 'purple';
    if (type.includes('damage')) return 'red';
    if (type.includes('vin')) return 'orange';
    if (type.includes('odometer')) return 'pink';
    return 'red'; // Default color
  }, []);

  // Focus on a specific marker
  const focusMarker = useCallback((image) => {
    setSelectedMarker(image);
  }, []);

  const showMapForImage = (image) => {
    if (image) focusMarker(image);
    setLocationDialogOpen(true);
  };

  const stopLockTimer = () => {
    if (lockTimerRef.current) window.clearInterval(lockTimerRef.current);
    lockTimerRef.current = null;
  };

  const clearLocalLock = (reference = caseData?.web_reference_number) => {
    if (reference) window.sessionStorage.removeItem(`case_lock_${reference}`);
    stopLockTimer();
    setIsLocked(false);
    setLockedBy('');
    setLockedTime(null);
  };

  const downloadAiReport = async () => {
    const reference = caseData?.web_reference_number;
    if (!reference) return;
    try {
      setAiReportLoading(true);
      const blob = await caseService.generateAiPdfReport(reference);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai_assessment_report_${reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbarMessage('AI assessment report downloaded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error downloading AI assessment report:', error);
      setSnackbarMessage('Failed to download AI assessment report');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setAiReportLoading(false);
    }
  };

  useEffect(() => {
    lockPromptShownRef.current = false;
    setLockConflict(null);
    setLockBlocked(true);
  }, [caseData?.web_reference_number]);

  // Animation effect when dialog opens
  useEffect(() => {
    if (open) {
      setAnimateIn(true);
      loadImages();

      // Check if the case is already locked, if not, lock it
      const initializeCaseLock = async () => {
        try {
          const lockStatus = await caseService.checkCaseLock(caseData.web_reference_number);

          // Get current user from localStorage
          const currentUser = JSON.parse(localStorage.getItem('user'));
          const username = currentUser?.username || 'Unknown User';

          // Store the current lock status in a component variable to avoid creating duplicate locks
          // This will help track if we've already processed this lock during the current session
          const isCurrentSessionLock = window.sessionStorage.getItem(`case_lock_${caseData.web_reference_number}`) === username;

          if (lockStatus.islocked) {
            // Case is already locked
            setIsLocked(true);
            setLockedBy(lockStatus.currentlockedby);
            setLockedTime(new Date(lockStatus.lockedat));

            // Calculate remaining time
            const lockTime = new Date(lockStatus.lockedat).getTime();
            const currentTime = new Date().getTime();
            const elapsedSeconds = Math.floor((currentTime - lockTime) / 1000);
            const remainingSeconds = Math.max(0, 15 * 60 - elapsedSeconds); // 15 minutes in seconds

            setRemainingTime(remainingSeconds);
            startLockTimer(remainingSeconds);

            // If locked by the same user (after browser refresh), allow them to continue
            if (lockStatus.currentlockedby === username) {
              // It's the same user who locked the case, allow them to continue
              console.log('Case already locked by the current user, continuing...');

              // Store the lock in session storage to track it across component re-renders
              window.sessionStorage.setItem(`case_lock_${caseData.web_reference_number}`, username);

              setLockBlocked(false);
              if (!lockPromptShownRef.current) {
                lockPromptShownRef.current = true;
                setResumeLockDialogOpen(true);
              }
            } else {
              setLockBlocked(true);
              // Locked by someone else, show warning and prevent modifications
              setSnackbarMessage(`This case is currently locked by ${lockStatus.currentlockedby}. Please try again later.`);
              setSnackbarSeverity('error');
              setSnackbarOpen(true);

              // Disable editing functionality here
              // This will be handled by the individual edit functions checking isLocked and lockedBy
            }
          } else {
            // Case is not locked on the server, but check if we have a session lock
            if (isCurrentSessionLock) {
              // This is an edge case: user has a session lock but server shows no lock
              // This could happen if the lock expired on the server but the session still has it
              console.log('User has a session lock but server shows no lock. Creating a new server lock.');
              // Create a new lock on the server to match the session lock
              await createNewLock(username);
            } else {
              // Normal case - no session lock and no server lock
              lockCase();
            }
          }
        } catch (error) {
          console.error('Error initializing case lock:', error);
          const errorData = error.response?.data;
          if (errorData?.code === 'USER_ALREADY_HAS_LOCK') {
            setLockBlocked(true);
            setLockConflict(errorData);
          }
        }
      };

      if (!readOnly && caseData && caseData.web_reference_number) {
        initializeCaseLock();
      } else if (readOnly) {
        setLockBlocked(true);
      }
    } else {
      setAnimateIn(false);
      setLocationDialogOpen(false);

      // Don't unlock the case when dialog is closed
      // Only clean up the timer UI, but keep the lock active
      stopLockTimer();
    }

    return () => {
      // Clean up timer when component unmounts
      stopLockTimer();
    };
  }, [open, caseData, readOnly]);

  // Check case lock status
  const checkCaseLockStatus = async () => {
    if (!caseData || !caseData.web_reference_number) return;

    try {
      const response = await caseService.checkCaseLock(caseData.web_reference_number);

      if (response.islocked) {
        setIsLocked(true);
        setLockedBy(response.currentlockedby);
        setLockedTime(new Date(response.lockedat));

        // Calculate remaining time
        const lockTime = new Date(response.lockedat).getTime();
        const currentTime = new Date().getTime();
        const elapsedSeconds = Math.floor((currentTime - lockTime) / 1000);
        const remainingSeconds = Math.max(0, 15 * 60 - elapsedSeconds); // 15 minutes in seconds

        setRemainingTime(remainingSeconds);
        startLockTimer(remainingSeconds);
      } else {
        setIsLocked(false);
        setLockedBy('');
        setLockedTime(null);
        setRemainingTime(15 * 60); // Reset to 15 minutes
      }
    } catch (error) {
      console.error('Error checking case lock status:', error);
      setSnackbarMessage('Error checking case lock status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Lock the case
  const lockCase = async () => {
    if (!caseData || !caseData.web_reference_number) return;

    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const username = currentUser?.username || 'Unknown User';

      // Check if this user already has a lock in the current session
      const isCurrentSessionLock = window.sessionStorage.getItem(`case_lock_${caseData.web_reference_number}`) === username;

      if (isCurrentSessionLock) {
        console.log('User already has a lock for this case in the current session');

        // Always check the server for the current lock status to get the correct remaining time
        const lockStatus = await caseService.checkCaseLock(caseData.web_reference_number);

        if (lockStatus.islocked && lockStatus.currentlockedby === username) {
          // Update UI with the server's lock information
          setIsLocked(true);
          setLockedBy(username);
          setLockedTime(new Date(lockStatus.lockedat));

          // Calculate remaining time based on server's lock time
          const lockTime = new Date(lockStatus.lockedat).getTime();
          const currentTime = new Date().getTime();
          const elapsedSeconds = Math.floor((currentTime - lockTime) / 1000);
          const remainingSeconds = Math.max(0, 15 * 60 - elapsedSeconds); // 15 minutes in seconds

          console.log(`Lock time from server: ${new Date(lockStatus.lockedat)}, remaining seconds: ${remainingSeconds}`);

          setRemainingTime(remainingSeconds);
          startLockTimer(remainingSeconds);
        } else {
          // If the server doesn't have a lock for this user (expired or unlocked), create a new one
          await createNewLock(username);
        }

        // Don't show any notification since the user already has a lock
        return;
      }

      // Create a new lock only if there's no session lock
      await createNewLock(username);
    } catch (error) {
      console.error('Error locking case:', error);
      const errorData = error.response?.data;
      if (errorData?.code === 'USER_ALREADY_HAS_LOCK') {
        setLockBlocked(true);
        setLockConflict(errorData);
      }
      setSnackbarMessage(errorData?.message || 'Error locking case');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Helper function to create a new lock
  const createNewLock = async (username) => {
    const response = await caseService.lockCase(caseData.web_reference_number, username);

    if (response.success) {
      // Store the lock in session storage to track it across component re-renders
      window.sessionStorage.setItem(`case_lock_${caseData.web_reference_number}`, username);

      setIsLocked(true);
      setLockedBy(username);
      setLockedTime(new Date());
      setRemainingTime(15 * 60); // 15 minutes in seconds
      setLockBlocked(false);
      startLockTimer(15 * 60);

      setSnackbarMessage('Case locked successfully. You have 15 minutes to complete your actions.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage(response.message || 'Failed to lock case');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Unlock the case
  const unlockCase = async () => {
    if (!caseData || !caseData.web_reference_number) return;

    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const username = currentUser?.username || 'Unknown User';

      const response = await caseService.unlockCase(caseData.web_reference_number, username);

      if (response.success) {
        // Remove the lock from session storage
        window.sessionStorage.removeItem(`case_lock_${caseData.web_reference_number}`);

        setIsLocked(false);
        setLockedBy('');
        setLockedTime(null);
        setLockBlocked(false);
        setResumeLockDialogOpen(false);

        // Clear the timer
        stopLockTimer();

        setSnackbarMessage('Case unlocked successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Close the dialog when case is unlocked
        onClose();
      } else {
        setSnackbarMessage(response.message || 'Failed to unlock case');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error unlocking case:', error);
      setSnackbarMessage('Error unlocking case');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Start the lock timer
  const startLockTimer = (initialSeconds) => {
    // Clear any existing timer
    stopLockTimer();

    // Set initial remaining time
    setRemainingTime(initialSeconds);

    // Start a new timer
    const timerId = setInterval(() => {
      setRemainingTime(prevTime => {
        if (prevTime <= 1) {
          // Time's up, clear the interval
          clearInterval(timerId);
          // Auto-unlock the case
          unlockCase();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Save the timer ID for cleanup
    lockTimerRef.current = timerId;
  };

  // Check if the current user can modify the case
  const canModifyCase = () => {
    // If case is not locked, anyone can modify it
    if (!isLocked) return true;

    // Get current user from localStorage
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const username = currentUser?.username || 'Unknown User';

    // Only the user who locked the case can modify it
    return lockedBy === username;
  };

  // Format the remaining time as MM:SS
  const formatRemainingTime = () => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Fetch images for the case
  const loadImages = async (silent = false) => {
    if (!caseData) return;

    try {
      if (!silent) setLoading(true);

      // Get the reference ID from the case data
      const refId = caseData.web_reference_number || caseData.case_id || caseData.id;

      if (!refId) {
        console.error('No reference ID available for fetching images');
        return;
      }

      // Call API to get images for this case with all versions
      console.log('Calling API with reference ID:', refId);
      const response = await caseService.getCaseImages(refId, true); // true = include all versions
      console.log('API response with all versions:', response);

      // Log the raw data to check all versions
      if (response) {
        console.log('Raw image data from API (all versions):', JSON.stringify(response, null, 2));
      }

      // Process images and organize by photo type
      const imagesByType = {};

      // Helper function to process a single image
      const processImage = (img) => {
        if (!img) return null;

        return {
          id: img.id || `img-${Math.random().toString(36).substr(2, 9)}`,
          type: img.photoType || img.type || 'Inspection Photo',
          url: img.azureUrl || img.imageUrl || img.filePath || img.url || null,
          timestamp: img.capturedAt || img.timestamp || new Date().toISOString(),
          status: 'Verified',
          version: img.version || 1,
          remarks: img.remarks,
          reassignmentRemark: img.reassignmentRemark,
          remarkedBy: img.remarkedBy,
          remarkedAt: img.remarkedAt,
          aiQualityCheck: img.aiQualityCheck,
          aiDamageAssessment: img.aiDamageAssessment,
          aiObservation: img.aiObservation,
          additionalRemarks: img.additionalRemarks,
          location: {
            lat: parseFloat(img.latitude || 0),
            lng: parseFloat(img.longitude || 0)
          }
        };
      };

      // Process images based on response format
      let allImages = [];

      if (Array.isArray(response)) {
        allImages = response.map(processImage).filter(img => img !== null);
      } else if (response && response.data && Array.isArray(response.data)) {
        allImages = response.data.map(processImage).filter(img => img !== null);
      } else if (response && response.data && response.data.images && Array.isArray(response.data.images)) {
        allImages = response.data.images.map(processImage).filter(img => img !== null);
      }

      console.log('All processed images:', allImages);

      // Group images by photo type
      allImages.forEach(img => {
        if (!imagesByType[img.type]) {
          imagesByType[img.type] = [];
        }
        imagesByType[img.type].push(img);
      });

      // For each photo type, sort by version (descending) and take the latest version
      const latestVersionImages = Object.keys(imagesByType).map(type => {
        // Sort versions in descending order
        const sortedVersions = imagesByType[type].sort((a, b) => b.version - a.version);
        // Take the latest version (first after sorting)
        const latestVersion = sortedVersions[0];

        // Add a property to indicate if this image has multiple versions
        latestVersion.hasMultipleVersions = sortedVersions.length > 1;
        latestVersion.allVersions = sortedVersions;

        // Add a property to indicate if this image has been reassigned (version > 1)
        latestVersion.isReassigned = latestVersion.version > 1;

        console.log(`Photo type ${type}: Latest version = ${latestVersion.version}, Has multiple versions = ${latestVersion.hasMultipleVersions}, Is reassigned = ${latestVersion.isReassigned}`);

        return latestVersion;
      });

      // Set the processed images to state
      setImages(latestVersionImages);
      setSelectedImage(current => {
        if (current) {
          const refreshed = latestVersionImages.find(image => image.id === current.id);
          if (refreshed) return refreshed;
        }
        return latestVersionImages[0] || null;
      });
      console.log('Final processed images with version info:', latestVersionImages);
    } catch (error) {
      console.error('Error fetching images:', error);
      if (!silent) {
        setSnackbarMessage('Error fetching images');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const aiAssessmentPending = images.length > 0 && images.some(image => !hasAiAssessment(image));

  useEffect(() => {
    if (!open || !aiAssessmentPending) return undefined;
    const pollId = window.setInterval(() => loadImages(true), 10000);
    return () => window.clearInterval(pollId);
  }, [open, aiAssessmentPending, caseData?.web_reference_number]);

  // Fetch image version history
  const fetchImageVersionHistory = async (photoType) => {
    try {
      setLoading(true);
      const refId = caseData.web_reference_number || caseData.case_id || caseData.id;

      if (!refId) {
        console.error('No reference ID available for fetching image history');
        return;
      }

      console.log(`Fetching version history for ${photoType} with reference ID: ${refId}`);

      // Call API to get image version history
      const response = await caseService.getImageVersionHistory(refId, photoType);
      console.log('Image version history response:', response);

      if (response && Array.isArray(response.data)) {
        // Sort versions in descending order (newest first)
        const sortedVersions = response.data.sort((a, b) => b.version - a.version);

        setVersionHistoryDialog(prev => ({
          ...prev,
          versions: sortedVersions
        }));

        console.log(`Found ${sortedVersions.length} versions for ${photoType}`);
        return sortedVersions;
      } else {
        console.log('No version history found or invalid response format');
        setVersionHistoryDialog(prev => ({
          ...prev,
          versions: []
        }));
        return [];
      }
    } catch (error) {
      console.error('Error fetching image version history:', error);
      setSnackbarMessage('Failed to load image history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle opening version history dialog
  const handleOpenVersionHistoryDialog = async (image) => {
    // Keep the current capture in the history so the reassignment journey reads end to end.
    const currentVersion = image.version || 1;

    // Debug the image object to see what versions are available
    console.log('Image object in handleOpenVersionHistoryDialog:', image);
    console.log('Current version:', currentVersion);
    console.log('All versions available:', image.allVersions);

    // Make sure we're comparing numbers, not strings
    const previousVersions = (image.allVersions || [])
      .filter(ver => Number(ver.version) !== Number(currentVersion))
      .sort((a, b) => Number(b.version) - Number(a.version)); // Sort by version in descending order

    const historyVersions = [
      { ...image, version: currentVersion },
      ...previousVersions
    ].sort((a, b) => Number(b.version) - Number(a.version));

    console.log('Filtered previous versions:', previousVersions);

    setVersionHistoryDialog({
      open: true,
      photoType: image.type,
      currentVersion: currentVersion,
      versions: historyVersions
    });

    console.log('Opening version history dialog with versions:', historyVersions);
  };

  // Handle closing version history dialog
  const handleCloseVersionHistoryDialog = () => {
    setVersionHistoryDialog({
      open: false,
      photoType: null,
      currentVersion: null,
      versions: []
    });
  };

  // Handle opening the action dialog
  const handleOpenActionDialog = (action) => {
    if (action === 'reassign') {
      // Enter reassignment mode instead of opening dialog
      setIsReassignMode(true);
      setSelectedImagesToRecapture([]);
      setIndividualRemarks(images.reduce((currentRemarks, image) => ({
        ...currentRemarks,
        [image.id]: imageRemarks[image.id]?.remark ?? image.remarks ?? ''
      }), {}));
    } else {
      // For recommend and notRecommend, use the dialog
      setActionType(action);
      setRemarks('');
      setActionDialogOpen(true);
    }
  };

  // Handle canceling reassignment mode
  const handleCancelReassign = () => {
    setIsReassignMode(false);
    setReassignmentDialogOpen(false);
    setSelectedImagesToRecapture([]);
    setIndividualRemarks({});
  };

  // Handle image selection for reassignment
  const handleImageSelection = (imageId) => {
    setSelectedImagesToRecapture(prev => {
      if (prev.includes(imageId)) {
        // Remove from selection
        return prev.filter(id => id !== imageId);
      } else {
        // Add to selection
        return [...prev, imageId];
      }
    });
  };

  // Handle setting remarks for a selected image during reassignment
  const handleReassignmentRemark = (imageId, remark) => {
    setIndividualRemarks(prev => ({
      ...prev,
      [imageId]: remark
    }));
  };

  // Handle closing action dialog
  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
  };

  const savePendingImageRemarks = async () => {
    const pendingRemarks = Object.entries(imageRemarks);
    if (!pendingRemarks.length) return;

    const username = JSON.parse(localStorage.getItem('user') || 'null')?.username || 'unknown';
    await Promise.all(pendingRemarks.map(([mediaId, value]) => caseService.saveImageRemark(
      caseData.web_reference_number,
      {
        mediaId,
        remark: value.remark ?? '',
        remarkedBy: username,
        remarkedAt: new Date().toISOString()
      }
    )));
    setImageRemarks({});
  };

  const releaseCaseLock = async (caseReference) => {
    try {
      if (isLocked) {
        const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
        await caseService.unlockCase(caseReference, currentUser?.username || 'Unknown User');
      }
    } catch (unlockError) {
      console.error('Error releasing case lock:', unlockError);
    } finally {
      clearLocalLock(caseReference);
    }
  };

  // Handle submitting an action
  const handleSubmitAction = async () => {
    // Extract the case reference number from the case data
    const caseReference = caseData?.web_reference_number;

    console.log('Case data for submission:', caseData);
    console.log('Using case reference:', caseReference);

    if (!caseReference) {
      console.error('Missing case reference number');
      setSnackbarMessage('Error: Missing case reference number');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      // Submit regular action (recommend or not recommend)
      const newStatus = actionType === 'recommend' ? 'Recommended' : 'Not Recommended';

      console.log('Submitting action:', {
        status: newStatus,
        remarks,
        type: actionType,
        caseReference: caseReference
      });

      try {
        await savePendingImageRemarks();

        // Call the API to update the case status with remarks
        await caseService.updateCaseStatus(caseReference, {
          status: newStatus,
          remarks: remarks,
          type: actionType
        });
        console.log('Case status updated successfully');

        await releaseCaseLock(caseReference);
      } catch (error) {
        console.error('Error updating case status:', error);
        throw error; // Re-throw to be caught by the outer try-catch
      }

      // Show success message
      setSnackbarMessage(`Case ${actionType === 'recommend' ? 'recommended' : 'not recommended'} successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Close the action dialog
      setActionDialogOpen(false);

      // Close the main case dialog
      onClose();
      onStatusChanged?.();
    } catch (error) {
      console.error('Error submitting action:', error);
      setSnackbarMessage(`Failed to ${actionType} case. Please try again.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle submitting reassignment from gallery view
  const handleSubmitReassignment = async () => {
    // Extract the case reference number from the case data
    const caseReference = caseData?.web_reference_number;

    if (!caseReference) {
      console.error('Missing case reference number');
      setSnackbarMessage('Error: Missing case reference number');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validate that at least one image is selected
    if (selectedImagesToRecapture.length === 0) {
      setSnackbarMessage('Please select at least one image to reassign');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    // Validate that all selected images have remarks
    const missingRemarks = selectedImagesToRecapture.filter(imageId =>
      !individualRemarks[imageId] || individualRemarks[imageId].trim() === ''
    );

    if (missingRemarks.length > 0) {
      setSnackbarMessage('Please provide remarks for all selected images');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      setReassignLoading(true);
      await savePendingImageRemarks();

      // Submit with individual image selections and remarks
      const reassignmentData = {
        type: 'reassign',
        selectedImages: selectedImagesToRecapture.map(imageId => {
          const image = images.find(img => (img.id || images.indexOf(img)) === imageId);
          return {
            mediaId: image.id,
            photoType: image.type,
            remarks: individualRemarks[imageId] || '',
            // These will be empty in the new version as per requirements
            filePath: '',
            latitude: null,
            longitude: null
          };
        }),
        remarkedBy: JSON.parse(localStorage.getItem('user') || 'null')?.username || 'Supervisor'
      };

      console.log('Submitting reassignment with individual images:', reassignmentData);
      const reassignmentResponse = await caseService.reassignCaseImages(caseReference, reassignmentData);

      await releaseCaseLock(caseReference);

      // Show success message
      setSnackbarMessage(reassignmentResponse.emailSent
        ? `Case reassigned and email sent to ${reassignmentResponse.emailRecipient}`
        : `Case reassigned, but email was not sent: ${reassignmentResponse.emailError || 'customer email unavailable'}`);
      setSnackbarSeverity(reassignmentResponse.emailSent ? 'success' : 'warning');
      setSnackbarOpen(true);

      // Exit reassignment mode
      setIsReassignMode(false);
      setReassignmentDialogOpen(false);

      // Close the main case dialog
      onClose();

      // Refresh the window after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500); // 1.5 second delay to allow the user to see the success message
    } catch (error) {
      console.error('Error submitting reassignment:', error);
      setSnackbarMessage('Failed to reassign images. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setReassignLoading(false);
    }
  };

  if (!caseData) return null;

  const activeImage = selectedImage || images[0] || null;
  const activeImageIndex = activeImage
    ? Math.max(0, images.findIndex(image => image.id === activeImage.id))
    : 0;
  const activeRemark = activeImage
    ? imageRemarks[activeImage.id]?.remark ?? activeImage.remarks ?? ''
    : '';
  const activeImageLabel = activeImage?.type
    ? t(`inspection.photoLabels.${activeImage.type}`, {
      defaultValue: activeImage.type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    })
    : '';
  const activeImageUrl = activeImage?.url
    ? (activeImage.url.startsWith('http') ? activeImage.url : `${API_URL}${activeImage.url}`)
    : '/images/placeholder-image.png';
  const activeAiPending = activeImage && !hasAiAssessment(activeImage);
  const completedReassignmentRemarks = selectedImagesToRecapture.filter(
    imageId => individualRemarks[imageId]?.trim()
  ).length;
  const selectedReassignmentImages = images.filter(image =>
    selectedImagesToRecapture.includes(image.id)
  );
  const modificationBlocked = lockBlocked || !canModifyCase();

  const selectAdjacentImage = (direction) => {
    const nextIndex = (activeImageIndex + direction + images.length) % images.length;
    setSelectedImage(images[nextIndex]);
  };

  const handleMagnifierMove = (event) => {
    if (event.pointerType !== 'mouse') return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const image = event.currentTarget.querySelector('img');
    if (!image?.naturalWidth || !image?.naturalHeight) return;

    const scale = Math.min(bounds.width / image.naturalWidth, bounds.height / image.naturalHeight);
    const displayedWidth = image.naturalWidth * scale;
    const displayedHeight = image.naturalHeight * scale;
    const offsetX = (bounds.width - displayedWidth) / 2;
    const offsetY = (bounds.height - displayedHeight) / 2;
    const pointerX = event.clientX - bounds.left;
    const pointerY = event.clientY - bounds.top;

    if (
      pointerX < offsetX || pointerX > offsetX + displayedWidth ||
      pointerY < offsetY || pointerY > offsetY + displayedHeight
    ) {
      setMagnifier(current => ({ ...current, visible: false }));
      return;
    }

    const lensRadius = 88;
    setMagnifier({
      visible: true,
      x: ((pointerX - offsetX) / displayedWidth) * 100,
      y: ((pointerY - offsetY) / displayedHeight) * 100,
      left: Math.min(Math.max(pointerX, lensRadius), bounds.width - lensRadius),
      top: Math.min(Math.max(pointerY, lensRadius), bounds.height - lensRadius)
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          overflow: 'hidden',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      TransitionProps={{
        timeout: 300
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: { xs: 1.5, md: 3 },
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        {/* Left side - Title and reference */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          transform: animateIn ? 'translateY(0)' : 'translateY(-20px)',
          opacity: animateIn ? 1 : 0,
          transition: 'all 0.4s ease'
        }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              mr: 1.5
            }}
          >
            <CheckCircle sx={{ color: '#fff', fontSize: '1.3rem' }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600
              }}
            >
              {caseData.status} {t('app.inspections')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                mt: 0.5
              }}
            >
              <Box component="span" sx={{ mr: 1, opacity: 0.7 }}>{t('caseDetails.referenceNumber')}:</Box>
              {caseData.web_reference_number}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          {isLocked && (
            <>
              <Chip
                icon={<LockIcon />}
                label={`Locked by ${lockedBy}`}
                color="warning"
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'flex' }, fontWeight: 600 }}
              />
              <Chip
                icon={<AccessTime />}
                label={formatRemainingTime()}
                variant="outlined"
              />
              {canModifyCase() && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  onClick={unlockCase}
                >
                  {t('common.unlock')}
                </Button>
              )}
            </>
          )}
          <Tooltip title="Close inspection">
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close inspection"
              sx={{ bgcolor: 'action.hover', width: 40, height: 40, borderRadius: 1 }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Content area */}
      <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            opacity: animateIn ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            images.length > 0 && activeImage ? (
              <>
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="h6">Inspection photos</Typography>
                    <Typography variant="body2" color="text.secondary">Select a photo to review its AI and user remarks.</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={() => showMapForImage(activeImage)}
                    sx={{ flexShrink: 0 }}
                  >
                    View location
                  </Button>
                </Box>
                {aiAssessmentPending && (
                  <Alert
                    severity="info"
                    icon={<CircularProgress size={18} />}
                    sx={{ mb: 1.5, alignItems: 'center' }}
                  >
                    <Typography variant="body2" fontWeight={600}>AI assessment is in progress</Typography>
                    <Typography variant="caption">Results usually arrive within 3-5 minutes. This page refreshes automatically.</Typography>
                  </Alert>
                )}
                <Paper
                variant="outlined"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '104px minmax(0, 1fr) 340px' },
                  gridTemplateAreas: {
                    xs: '"thumbs" "photo" "details"',
                    md: '"thumbs photo details"'
                  },
                  minHeight: { md: 'calc(100vh - 256px)' },
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.paper'
                }}
                >
                <Box
                  component="nav"
                  aria-label="Inspection photos"
                  sx={{
                    gridArea: 'thumbs',
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 1,
                    p: 1.5,
                    overflow: 'auto',
                    borderRight: { md: 1 },
                    borderBottom: { xs: 1, md: 0 },
                    borderColor: 'divider',
                    bgcolor: 'background.default'
                  }}
                >
                  {images.map((image, index) => {
                    const isActive = image.id === activeImage.id;
                    const imageUrl = image.url
                      ? (image.url.startsWith('http') ? image.url : `${API_URL}${image.url}`)
                      : '/images/placeholder-image.png';
                    const isSelectedForReassignment = selectedImagesToRecapture.includes(image.id);
                    return (
                      <Box
                        key={image.id}
                        sx={{
                          width: { xs: 76, md: '100%' },
                          minWidth: { xs: 76, md: 0 },
                          height: 68,
                          position: 'relative'
                        }}
                      >
                        <Tooltip title={image.type || `Photo ${index + 1}`} placement="right">
                          <Box
                            component="button"
                            type="button"
                            onClick={() => {
                              setSelectedImage(image);
                              setEditingRemarkId(null);
                              setTempRemark('');
                            }}
                            aria-label={`Show ${image.type || `photo ${index + 1}`}`}
                            aria-current={isActive ? 'true' : undefined}
                            sx={{
                              width: '100%',
                              height: '100%',
                              p: 0.5,
                              border: 2,
                              borderColor: isSelectedForReassignment ? 'warning.main' : isActive ? 'primary.main' : 'divider',
                              borderRadius: 1.5,
                              bgcolor: 'background.paper',
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': { borderColor: isReassignMode ? 'warning.main' : 'primary.light' },
                              '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 }
                            }}
                          >
                            <Box
                              component="img"
                              src={imageUrl}
                              alt=""
                              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', borderRadius: 1 }}
                              onError={(event) => { event.currentTarget.src = '/images/placeholder-image.png'; }}
                            />
                            <Box
                              component="span"
                              sx={{
                                position: 'absolute',
                                right: 4,
                                bottom: 4,
                                minWidth: 20,
                                height: 20,
                                px: 0.5,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1,
                                bgcolor: isActive ? 'primary.main' : 'text.primary',
                                color: 'primary.contrastText',
                                fontSize: 12,
                                fontWeight: 600
                              }}
                            >
                              {index + 1}
                            </Box>
                          </Box>
                        </Tooltip>
                        {isReassignMode && (
                          <Checkbox
                            checked={isSelectedForReassignment}
                            onChange={() => {
                              setSelectedImage(image);
                              handleImageSelection(image.id);
                            }}
                            inputProps={{ 'aria-label': `Select ${image.type || `photo ${index + 1}`} for reassignment` }}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 2,
                              left: 2,
                              zIndex: 1,
                              p: 0.25,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              color: 'warning.main',
                              '&.Mui-checked': { color: 'warning.dark' }
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    gridArea: 'photo',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: { md: 1 },
                    borderBottom: { xs: 1, md: 0 },
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" noWrap>{activeImageLabel}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Photo {activeImageIndex + 1} of {images.length}
                        {activeImage.timestamp ? ` • ${new Date(activeImage.timestamp).toLocaleString()}` : ''}
                      </Typography>
                    </Box>
                    {activeImage.hasMultipleVersions && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AccessTime />}
                        onClick={() => handleOpenVersionHistoryDialog(activeImage)}
                      >
                        History
                      </Button>
                    )}
                  </Box>
                  <Box
                    onPointerMove={handleMagnifierMove}
                    onPointerLeave={() => setMagnifier(current => ({ ...current, visible: false }))}
                    sx={{
                      flex: 1,
                      minHeight: { xs: 360, md: 520 },
                      p: { xs: 1, sm: 2 },
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'grey.100',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={activeImageUrl}
                      alt={activeImageLabel}
                      sx={{ width: '100%', height: '100%', maxHeight: 'calc(100vh - 330px)', objectFit: 'contain' }}
                      onError={(event) => { event.currentTarget.src = '/images/placeholder-image.png'; }}
                    />
                    {images.length > 1 && (
                      <>
                        <Tooltip title="Previous photo">
                          <IconButton
                            onClick={() => selectAdjacentImage(-1)}
                            aria-label="Previous photo"
                            sx={{
                              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                              bgcolor: 'background.paper', boxShadow: 2, zIndex: 3,
                              '&:hover': { bgcolor: 'background.paper' }
                            }}
                          >
                            <ChevronLeft />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Next photo">
                          <IconButton
                            onClick={() => selectAdjacentImage(1)}
                            aria-label="Next photo"
                            sx={{
                              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                              bgcolor: 'background.paper', boxShadow: 2, zIndex: 3,
                              '&:hover': { bgcolor: 'background.paper' }
                            }}
                          >
                            <ChevronRight />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {magnifier.visible && (
                      <Box
                        aria-hidden="true"
                        sx={{
                          display: { xs: 'none', md: 'block' },
                          position: 'absolute',
                          left: magnifier.left,
                          top: magnifier.top,
                          width: 176,
                          height: 176,
                          transform: 'translate(-50%, -50%)',
                          borderRadius: '50%',
                          border: 3,
                          borderColor: 'background.paper',
                          boxShadow: 4,
                          backgroundImage: `url("${activeImageUrl}")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '260%',
                          backgroundPosition: `${magnifier.x}% ${magnifier.y}%`,
                          pointerEvents: 'none',
                          zIndex: 2
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderTop: 1, borderColor: 'divider' }}>
                    <ZoomIn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      Hover to magnify
                    </Typography>
                    {activeImage.location && (activeImage.location.lat !== 0 || activeImage.location.lng !== 0) && (
                      <Button
                        size="small"
                        startIcon={<LocationOn />}
                        onClick={() => showMapForImage(activeImage)}
                      >
                        View location
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box sx={{ gridArea: 'details', p: 2, overflowY: 'auto' }}>
                  <Box sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: 'info.light' }}>
                    <Typography variant="caption" color="text.secondary">Selected photo</Typography>
                    <Typography variant="h6">{activeImageLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remarks below apply only to this photo.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <SmartToy color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>AI remarks</Typography>
                    </Box>
                    {!activeAiPending ? (
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Image quality</Typography>
                          <Typography variant="body2" fontWeight={600}>{activeImage.aiQualityCheck || 'Not provided'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Damage assessment</Typography>
                          <Typography variant="body2" fontWeight={600}>{activeImage.aiDamageAssessment || 'Not provided'}</Typography>
                        </Box>
                        {(activeImage.aiObservation || activeImage.additionalRemarks) && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">AI remarks</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {activeImage.aiObservation || activeImage.additionalRemarks}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                        <CircularProgress size={22} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Processing this photo</Typography>
                          <Typography variant="caption" color="text.secondary">AI remarks will appear here automatically.</Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>

                  {['Recommended', 'Not Recommended', 'Approved', 'Rejected'].includes(caseData?.status) && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={aiReportLoading ? <CircularProgress size={18} /> : <PictureAsPdf />}
                      onClick={downloadAiReport}
                      disabled={aiReportLoading}
                      sx={{ mt: 1.5, justifyContent: 'flex-start' }}
                    >
                      {aiReportLoading ? 'Preparing AI report…' : 'Download AI assessment report'}
                    </Button>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {isReassignMode ? (
                    <Box>
                      <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'warning.light' }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {selectedImagesToRecapture.length} photo{selectedImagesToRecapture.length === 1 ? '' : 's'} selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          The customer will reupload only the checked photos.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Checkbox
                          checked={selectedImagesToRecapture.includes(activeImage.id)}
                          onChange={() => handleImageSelection(activeImage.id)}
                          inputProps={{ 'aria-label': `Select ${activeImageLabel} for reassignment` }}
                          color="warning"
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>Reassign {activeImageLabel}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Check this photo, then add its individual instruction.
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={selectedImagesToRecapture.includes(activeImage.id) ? 'warning' : 'default'}
                          label={selectedImagesToRecapture.includes(activeImage.id) ? 'Selected' : 'Not selected'}
                        />
                      </Box>
                      <Alert severity="info" variant="outlined">
                        After selecting all photos, use <strong>Review &amp; reassign</strong> to enter every mandatory remark in one window.
                      </Alert>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>Your remarks</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        For {activeImageLabel}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        label={`Remark for ${activeImageLabel}`}
                        placeholder={readOnly ? 'No image-level remark was added.' : `Add your observation for ${activeImageLabel}`}
                        value={activeRemark}
                        onChange={(event) => setImageRemarks(previous => ({
                          ...previous,
                          [activeImage.id]: {
                            ...previous[activeImage.id],
                            remark: event.target.value
                          }
                        }))}
                        InputProps={{ readOnly: readOnly || modificationBlocked }}
                      />
                      {(activeImage.remarkedBy || imageRemarks[activeImage.id]?.remarkedBy) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {t('common.remarkedBy', { name: activeImage.remarkedBy || imageRemarks[activeImage.id]?.remarkedBy })}
                        </Typography>
                      )}
                      {!readOnly && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Image remarks save automatically when you take a decision.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                </Paper>
              </>
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
                <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('common.noImagesAvailableDesc')}
                </Typography>
              </Box>
            )
          )}
        </Box>

      </Box>

      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <MapIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>Photo location</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {selectedMarker?.type ? selectedMarker.type.replace(/_/g, ' ') : 'Inspection photo'}
            </Typography>
          </Box>
          <IconButton onClick={() => setLocationDialogOpen(false)} aria-label="Close location">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: '0 !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 260px' }, height: { xs: '65vh', md: 560 } }}>
            <ImageLocationMap images={images} selectedImage={selectedMarker} />
            <Paper square elevation={0} sx={{ display: { xs: 'none', md: 'flex' }, minHeight: 0, flexDirection: 'column', borderLeft: 1, borderColor: 'divider' }}>
              <Box sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" fontWeight={700}>Captured locations</Typography>
                <Typography variant="caption" color="text.secondary">Choose another photo location</Typography>
              </Box>
              <Box sx={{ minHeight: 0, flex: 1, overflowY: 'auto' }}>
                <LocationListSidebar
                  images={images}
                  onLocationClick={focusMarker}
                  selectedImage={selectedMarker}
                  getMarkerColor={getMarkerColor}
                />
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="contained" onClick={() => setLocationDialogOpen(false)}>Close location</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resumeLockDialogOpen} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          Case already locked by you
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" variant="outlined">
            You still have this case locked with {formatRemainingTime()} remaining. Continue the review or unlock it now.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={unlockCase}>
            Unlock case
          </Button>
          <Button variant="contained" onClick={() => setResumeLockDialogOpen(false)}>
            Continue review
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(lockConflict)} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          Another case is already locked
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" variant="outlined">
            {lockConflict?.message}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => {
              setLockConflict(null);
              onClose();
            }}
          >
            Return to dashboard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reassignmentDialogOpen}
        onClose={() => !reassignLoading && setReassignmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <PersonAdd color="warning" />
          <Box>
            <Typography variant="h6">Reassign selected photos</Typography>
            <Typography variant="caption" color="text.secondary">
              Add one clear instruction for every photo the customer must reupload.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Alert
            severity={completedReassignmentRemarks === selectedReassignmentImages.length ? 'success' : 'warning'}
            sx={{ mb: 2 }}
          >
            {completedReassignmentRemarks} of {selectedReassignmentImages.length} mandatory remarks completed.
          </Alert>
          <Stack spacing={1.5}>
            {selectedReassignmentImages.map((image, index) => {
              const label = image.type
                ? image.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                : `Photo ${index + 1}`;
              const imageUrl = image.url
                ? (image.url.startsWith('http') ? image.url : `${API_URL}${image.url}`)
                : '/images/placeholder-image.png';
              const hasRemark = Boolean(individualRemarks[image.id]?.trim());

              return (
                <Paper key={image.id} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={label}
                    sx={{ width: 80, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    onError={(event) => { event.currentTarget.src = '/images/placeholder-image.png'; }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={`Mandatory remark for ${label}`}
                      placeholder="Tell the customer what must be corrected in this photo"
                      value={individualRemarks[image.id] || ''}
                      onChange={(event) => handleReassignmentRemark(image.id, event.target.value)}
                      required
                      error={!hasRemark}
                      helperText={hasRemark ? 'Ready' : 'Remark is required'}
                    />
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setReassignmentDialogOpen(false)} disabled={reassignLoading}>Back</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={reassignLoading ? <CircularProgress size={18} color="inherit" /> : <Send />}
            onClick={handleSubmitReassignment}
            disabled={reassignLoading}
          >
            {reassignLoading ? 'Submitting…' : 'Confirm reassignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          bgcolor: actionType === 'recommend' ? 'success.main' : actionType === 'notRecommend' ? 'error.main' : 'warning.main',
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {actionType === 'recommend' ? (
            <>
              <ThumbUp /> Recommend Case
            </>
          ) : actionType === 'notRecommend' ? (
            <>
              <ThumbDown /> Not Recommend Case
            </>
          ) : (
            <>
              <PersonAdd /> Reassign Case
            </>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {actionType === 'recommend' ? 'Please provide remarks for recommending this case:' :
              actionType === 'notRecommend' ? 'Please provide reasons for not recommending this case:' :
                'Please provide remarks for reassigning this case:'}
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            required={false} // Make remarks optional as per requirements
            placeholder={actionType === 'recommend' ? 'Optional: Add your recommendation remarks here' :
              actionType === 'notRecommend' ? 'Optional: Add your reasons for not recommending here' :
                'Optional: Add your reassignment remarks here'}
          />
          {actionType === 'reassign' && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Select images to recapture:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="selectAllImages"
                    checked={selectAllImages}
                    onChange={(e) => {
                      setSelectAllImages(e.target.checked);
                      if (e.target.checked) {
                        setSelectedImagesToRecapture([]);
                        setIndividualRemarks({});
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>All images</Typography>
                </Box>
              </Box>

              {!selectAllImages && (
                <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2, border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                  {images.map((image, index) => (
                    <Box key={image.id || index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <input
                        type="checkbox"
                        id={`image-${index}`}
                        checked={selectedImagesToRecapture.includes(image.id || index)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedImagesToRecapture([...selectedImagesToRecapture, image.id || index]);
                          } else {
                            setSelectedImagesToRecapture(selectedImagesToRecapture.filter(id => id !== (image.id || index)));
                            const newRemarks = { ...individualRemarks };
                            delete newRemarks[image.id || index];
                            setIndividualRemarks(newRemarks);
                          }
                        }}
                      />
                      <Box sx={{ ml: 1, flexGrow: 1 }}>
                        <Typography variant="body2">{image.type || `Image ${index + 1}`}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {!selectAllImages && selectedImagesToRecapture.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Individual remarks for selected images:</Typography>
                  {selectedImagesToRecapture.map((imageId) => {
                    const image = images.find(img => (img.id || images.indexOf(img)) === imageId);
                    return (
                      <Box key={imageId} sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {image ? (image.type || `Image ${images.indexOf(image) + 1}`) : `Image ${imageId}`}:
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Remarks for this image"
                          value={individualRemarks[imageId] || ''}
                          onChange={(e) => setIndividualRemarks({ ...individualRemarks, [imageId]: e.target.value })}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}

              {selectAllImages && (
                <TextField
                  autoFocus
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  label="Remarks for all images"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  required
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseActionDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAction}
            variant="contained"
            color={actionType === 'recommend' ? 'success' : actionType === 'notRecommend' ? 'error' : 'warning'}
            startIcon={<Send />}
            disabled={modificationBlocked}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={versionHistoryDialog.open}
        onClose={handleCloseVersionHistoryDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
          py: 2.25,
          background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{ width: 42, height: 42, bgcolor: 'primary.main' }}>
              <AccessTime />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="div" fontWeight={700}>
                Reassignment history
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {versionHistoryDialog.photoType && versionHistoryDialog.photoType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                {' • '}{versionHistoryDialog.versions.length} capture{versionHistoryDialog.versions.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseVersionHistoryDialog} size="small" aria-label="Close history">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'grey.50' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionHistoryDialog.versions.length > 0 ? (
            <Stack spacing={0}>
              {versionHistoryDialog.versions.map((version, index) => {
                const versionNumber = Number(version.version || 1);
                const isLatest = index === 0;
                const isOriginal = versionNumber === 1;
                const latitude = Number(version.latitude ?? version.location?.lat);
                const longitude = Number(version.longitude ?? version.location?.lng);
                const hasLocation = Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude !== 0 || longitude !== 0);
                const imageUrl = version.url || version.azureUrl || version.imageUrl || (version.fileUrl ? (version.fileUrl.startsWith('http') ? version.fileUrl : `https://${window.location.hostname}:5000${version.fileUrl}`) : '/images/placeholder-image.png');
                const capturedAt = version.timestamp || version.capturedAt || version.capturedat || version.updatedat || version.createdat;

                return (
                  <Box
                    key={`version-${versionNumber}-${index}`}
                    sx={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: 1.5 }}
                  >
                    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{
                        mt: 2.5,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: isLatest ? 'success.main' : 'background.paper',
                        border: 3,
                        borderColor: isLatest ? 'success.light' : 'grey.400',
                        zIndex: 1
                      }} />
                      {index < versionHistoryDialog.versions.length - 1 && (
                        <Box sx={{ position: 'absolute', top: 34, bottom: -10, width: 2, bgcolor: 'divider' }} />
                      )}
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        borderRadius: 2.5,
                        borderColor: isLatest ? 'success.light' : 'divider',
                        boxShadow: isLatest ? `0 6px 20px ${alpha(theme.palette.success.main, 0.12)}` : 'none'
                      }}
                    >
                      <Grid container>
                        <Grid item xs={12} sm={5}>
                          <Box sx={{ position: 'relative', height: { xs: 200, sm: '100%' }, minHeight: { sm: 210 }, bgcolor: 'grey.200' }}>
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={`Capture version ${versionNumber}`}
                              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                              onError={(event) => { event.currentTarget.src = '/images/placeholder-image.png'; }}
                            />
                            <Chip
                              label={isLatest ? 'Current photo' : isOriginal ? 'Original submission' : 'Earlier recapture'}
                              color={isLatest ? 'success' : 'default'}
                              size="small"
                              sx={{ position: 'absolute', top: 12, left: 12, fontWeight: 700, bgcolor: isLatest ? undefined : 'background.paper' }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={7}>
                          <Box sx={{ p: 2.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  Attempt {versionNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {isLatest ? 'Latest accepted capture' : isOriginal ? 'First customer capture' : 'Superseded after reassignment'}
                                </Typography>
                              </Box>
                              <Chip label={`v${versionNumber}`} size="small" variant="outlined" color={isLatest ? 'success' : 'default'} />
                            </Box>

                            <Stack spacing={0.75}>
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <AccessTime fontSize="small" />
                                {capturedAt ? new Date(capturedAt).toLocaleString() : 'Capture time unavailable'}
                              </Typography>
                              {version.capturedby && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <PersonAdd fontSize="small" />
                                  Captured by {version.capturedby}
                                </Typography>
                              )}
                            </Stack>

                            {(version.reassignmentRemark || version.remarks) && (
                              <Box sx={{ mt: 1.75, p: 1.25, display: 'flex', gap: 1, bgcolor: alpha(theme.palette.warning.light, 0.16), borderRadius: 1.5 }}>
                                <Comment fontSize="small" color="warning" sx={{ mt: 0.15 }} />
                                <Box>
                                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    {version.reassignmentRemark ? 'Reassignment instruction' : 'Image remark'}
                                  </Typography>
                                  <Typography variant="body2">{version.reassignmentRemark || version.remarks}</Typography>
                                </Box>
                              </Box>
                            )}

                            {hasLocation && (
                              <Button
                                size="small"
                                startIcon={<LocationOn />}
                                onClick={() => window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank', 'noopener,noreferrer')}
                                sx={{ mt: 1.25, px: 0 }}
                              >
                                View capture location
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No version history available for this image.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Action buttons at the bottom */}
      {!readOnly && <Box sx={{
        mt: 'auto',
        px: { xs: 1.5, md: 3 },
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'background.paper',
        position: 'sticky',
        bottom: 0,
        zIndex: 10
      }}>
        {isReassignMode ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '60%' }}>
              <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                Select all photos the customer must reupload
              </Typography>
              {selectedImagesToRecapture.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {selectedImagesToRecapture.length} selected • {completedReassignmentRemarks} of {selectedImagesToRecapture.length} individual remarks completed
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Close />}
                onClick={handleCancelReassign}
                size="small"
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={() => {
                  if (selectedImagesToRecapture.length === 0) {
                    setSnackbarMessage('Select at least one photo to reassign.');
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                    return;
                  }
                  setReassignmentDialogOpen(true);
                }}
                disabled={reassignLoading}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40
                }}
              >
                Review & reassign
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', ml: 'auto' }}>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PersonAdd />}
                onClick={() => handleOpenActionDialog('reassign')}
                disabled={modificationBlocked}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 132,
                minHeight: 40
              }}
            >
              Reassign
            </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ThumbDown />}
                onClick={() => handleOpenActionDialog('notRecommend')}
                disabled={modificationBlocked}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 160,
                minHeight: 40
              }}
            >
              Not Recommend
            </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ThumbUp />}
                onClick={() => handleOpenActionDialog('recommend')}
                disabled={modificationBlocked}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 140,
                minHeight: 40
              }}
            >
              Recommend
            </Button>
          </Box>
        )}
      </Box>}
    </Dialog>
  );
};

export default ProcessedCaseView;
